import { DateTime } from 'luxon';

// Comprehensive list of commonly used timezones grouped by region
export const COMMON_TIMEZONES = [
  // Pacific
  { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)' },
  { value: 'Pacific/Fiji', label: 'Fiji (FJT)' },
  { value: 'Pacific/Honolulu', label: 'Honolulu (HST)' },
  
  // Australia
  { value: 'Australia/Sydney', label: 'Sydney (AEDT/AEST)' },
  { value: 'Australia/Melbourne', label: 'Melbourne (AEDT/AEST)' },
  { value: 'Australia/Brisbane', label: 'Brisbane (AEST)' },
  { value: 'Australia/Perth', label: 'Perth (AWST)' },
  { value: 'Australia/Adelaide', label: 'Adelaide (ACDT/ACST)' },
  { value: 'Australia/Darwin', label: 'Darwin (ACST)' },
  { value: 'Australia/Hobart', label: 'Hobart (AEDT/AEST)' },
  
  // Asia
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Seoul', label: 'Seoul (KST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Bangkok', label: 'Bangkok (ICT)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Kolkata', label: 'Mumbai/Kolkata (IST)' },
  
  // Europe
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'Europe/Rome', label: 'Rome (CET/CEST)' },
  { value: 'Europe/Madrid', label: 'Madrid (CET/CEST)' },
  { value: 'Europe/Amsterdam', label: 'Amsterdam (CET/CEST)' },
  { value: 'Europe/Brussels', label: 'Brussels (CET/CEST)' },
  { value: 'Europe/Vienna', label: 'Vienna (CET/CEST)' },
  { value: 'Europe/Stockholm', label: 'Stockholm (CET/CEST)' },
  { value: 'Europe/Moscow', label: 'Moscow (MSK)' },
  
  // Americas
  { value: 'America/New_York', label: 'New York (EST/EDT)' },
  { value: 'America/Chicago', label: 'Chicago (CST/CDT)' },
  { value: 'America/Denver', label: 'Denver (MST/MDT)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
  { value: 'America/Toronto', label: 'Toronto (EST/EDT)' },
  { value: 'America/Vancouver', label: 'Vancouver (PST/PDT)' },
  { value: 'America/Mexico_City', label: 'Mexico City (CST)' },
  { value: 'America/Sao_Paulo', label: 'São Paulo (BRT)' },
  { value: 'America/Buenos_Aires', label: 'Buenos Aires (ART)' },
  
  // Africa
  { value: 'Africa/Cairo', label: 'Cairo (EET)' },
  { value: 'Africa/Johannesburg', label: 'Johannesburg (SAST)' },
  { value: 'Africa/Lagos', label: 'Lagos (WAT)' },
  { value: 'Africa/Nairobi', label: 'Nairobi (EAT)' },
];

/**
 * Get the user's timezone based on browser detection
 * Uses Intl.DateTimeFormat().resolvedOptions().timeZone for accurate timezone detection
 * @returns {Promise<string>} IANA timezone string (e.g., 'Australia/Sydney')
 */
export const detectUserTimezone = async () => {
  return new Promise((resolve) => {
    try {
      // Use browser's built-in timezone detection (most reliable)
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || DateTime.local().zoneName;
      console.log('Detected timezone from browser:', browserTimezone);
      
      // Verify the timezone exists in our common timezones list
      const timezoneExists = COMMON_TIMEZONES.some(tz => tz.value === browserTimezone);
      
      if (timezoneExists) {
        resolve(browserTimezone);
      } else {
        // If detected timezone not in our list, try to find a match by offset
        console.log('Detected timezone not in common list, finding closest match...');
        const detectedOffset = DateTime.now().setZone(browserTimezone).offset;
        
        // Find a timezone with matching offset
        const matchingTimezone = COMMON_TIMEZONES.find(tz => {
          const tzOffset = DateTime.now().setZone(tz.value).offset;
          return tzOffset === detectedOffset;
        });
        
        if (matchingTimezone) {
          console.log('Found matching timezone by offset:', matchingTimezone.value);
          resolve(matchingTimezone.value);
        } else {
          // Fall back to default
          console.log('No matching timezone found, using default: Australia/Sydney');
          resolve('Australia/Sydney');
        }
      }
    } catch (error) {
      console.error('Error detecting timezone:', error);
      // Fall back to default
      resolve('Australia/Sydney');
    }
  });
};

/**
 * Get current time in a specific timezone
 * @param {string} timezone - IANA timezone string
 * @returns {string} Formatted time string
 */
export const getCurrentTimeInTimezone = (timezone) => {
  try {
    return DateTime.now().setZone(timezone).toFormat('HH:mm');
  } catch (error) {
    console.error('Error getting time for timezone:', timezone, error);
    return '';
  }
};

/**
 * Get timezone offset display (e.g., "UTC+10:00")
 * @param {string} timezone - IANA timezone string
 * @returns {string} Formatted offset string
 */
export const getTimezoneOffset = (timezone) => {
  try {
    const dt = DateTime.now().setZone(timezone);
    const offset = dt.offset / 60; // Convert minutes to hours
    const sign = offset >= 0 ? '+' : '';
    const hours = Math.floor(Math.abs(offset));
    const minutes = Math.abs(offset) % 1 * 60;
    return `UTC${sign}${offset >= 0 ? hours : -hours}:${minutes.toString().padStart(2, '0')}`;
  } catch (error) {
    console.error('Error getting offset for timezone:', timezone, error);
    return '';
  }
};

/**
 * Format a date with timezone information
 * @param {string} dateStr - Date string
 * @param {string} timeStr - Time string
 * @param {string} timezone - IANA timezone string
 * @returns {string} Formatted date-time string with timezone
 */
export const formatDateTimeWithTimezone = (dateStr, timeStr, timezone) => {
  try {
    const dt = DateTime.fromISO(`${dateStr}T${timeStr}`, { zone: timezone });
    return dt.toFormat('ccc, dd MMM yyyy, HH:mm ZZZZ');
  } catch (error) {
    console.error('Error formatting date-time:', error);
    return `${dateStr} ${timeStr}`;
  }
};
